import { Component, OnInit, Input, ElementRef, ViewChild } from '@angular/core'
import { ViewerService } from '../../../../case-viewer/neuroglancer-frame/viewer.service'
import { HttpErrorResponse } from '@angular/common/http'
import { DownloadService } from '../../../services/download.service'
import { ToasterService } from '../../../services/toaster.service'
import Konva from 'konva'

@Component({
    selector: 'app-snapshot-canvas',
    templateUrl: './snapshot-canvas.component.html',
    styleUrls: ['./snapshot-canvas.component.scss'],
    standalone: true,
})
export class SnapshotCanvasComponent implements OnInit {
    @Input() switchSnapshotMode: () => void
    @ViewChild('containerDiv') container!: ElementRef

    currentlyDrawing: boolean = true
    adjustedY = 60 //topbar
    initialX: number
    initialY: number
    selectedX: number
    selectedY: number
    selectedWidth: number
    selectedHeight: number

    //konva
    stage: any
    rect: any
    rectGroup: any

    constructor(
        readonly viewer: ViewerService,
        private readonly downloader: DownloadService,
        private readonly toaster: ToasterService
    ) {}

    ngOnInit(): void {}

    resetDrawing(): void {
        this.currentlyDrawing = true
        this.selectedX = 0
        this.selectedY = 0
        this.selectedWidth = 0
        this.selectedHeight = 0
        this.stage.clear()
    }

    close(): void {
        this.switchSnapshotMode()
    }

    touchEventHandler(e: Event): void {
        if (e instanceof TouchEvent) {
            const touch = e.changedTouches[0]
            const mouseEvent = new MouseEvent(e.type.replace('touch', 'mouse'), {
                clientX: touch.clientX,
                clientY: touch.clientY,
                buttons: 1,
            })

            switch (e.type) {
                case 'touchstart':
                    this.mouseDown(mouseEvent)
                    break
                case 'touchmove':
                    this.mouseMove(mouseEvent)
                    break
                case 'touchend':
                    this.mouseUp()
                    break
            }
            e.preventDefault()
        }
    }

    mouseDown(e: MouseEvent) {
        e.preventDefault()
        if (!this.currentlyDrawing) return

        this.initialX = this.selectedX = e.clientX
        this.initialY = this.selectedY = e.clientY - this.adjustedY
        this.selectedWidth = 0
        this.selectedHeight = 0
    }

    mouseMove(e: MouseEvent) {
        if (!this.currentlyDrawing || !this.selectedY || !this.selectedX) return

        if (this.initialX > e.clientX) this.selectedX = e.clientX
        if (this.initialY > e.clientY) this.selectedY = e.clientY
        this.selectedWidth = Math.abs(this.initialX - e.clientX)
        this.selectedHeight = Math.abs(this.initialY - e.clientY + this.adjustedY)
    }

    mouseUp() {
        if (!this.currentlyDrawing) return
        this.currentlyDrawing = false

        this.rect = new Konva.Rect({
            x: this.selectedX,
            y: this.selectedY,
            width: this.selectedWidth,
            height: this.selectedHeight,
            stroke: 'yellow',
            strokeWidth: 2,
        })

        this.stage = new Konva.Stage({
            container: 'container',
            width: window.innerWidth,
            height: window.innerHeight,
        })

        const layer = new Konva.Layer()

        this.rectGroup = new Konva.Group({
            x: 0,
            y: 0,
            draggable: true,
        })

        layer.add(this.rectGroup)
        this.rectGroup.add(this.rect)
        this.addAnchor(this.rectGroup, this.selectedX, this.selectedY, 'topLeft')
        this.addAnchor(
            this.rectGroup,
            this.selectedX + this.selectedWidth,
            this.selectedY,
            'topRight'
        )
        this.addAnchor(
            this.rectGroup,
            this.selectedX + this.selectedWidth,
            this.selectedY + this.selectedHeight,
            'bottomRight'
        )
        this.addAnchor(
            this.rectGroup,
            this.selectedX,
            this.selectedY + this.selectedHeight,
            'bottomLeft'
        )

        this.stage.add(layer)
    }

    mouseScroll(e: MouseEvent) {
        e.preventDefault()
    }

    downloadSnapshot(): void {
        if (!this.selectedWidth) {
            this.toaster.error('Select area to take a snapshot', 'Failed to download snapshot.')
            return
        }
        const { x, y, width, height } = this.rect.attrs
        const { x: offsetX, y: offsetY } = this.rectGroup.attrs
        this.downloader
            .downloadSnapshot(window.innerWidth, window.innerHeight, [
                x + offsetX + (width > 0 ? 0 : width),
                y + offsetY + (height > 0 ? 0 : height) + this.adjustedY,
                x + offsetX + (width > 0 ? width : 0),
                y + offsetY + (height > 0 ? height : 0) + this.adjustedY,
            ])
            .subscribe({
                error: (err: HttpErrorResponse) => {
                    this.toaster.httpError(err, 'Failed to download snapshot.')
                },
            })
    }

    update(activeAnchor: any) {
        var group = activeAnchor.getParent()

        var topLeft = group.findOne('.topLeft')
        var topRight = group.findOne('.topRight')
        var bottomRight = group.findOne('.bottomRight')
        var bottomLeft = group.findOne('.bottomLeft')
        var image = group.findOne('Rect')

        var anchorX = activeAnchor.x()
        var anchorY = activeAnchor.y()

        // update anchor positions
        switch (activeAnchor.getName()) {
            case 'topLeft':
                topRight.y(anchorY)
                bottomLeft.x(anchorX)
                break
            case 'topRight':
                topLeft.y(anchorY)
                bottomRight.x(anchorX)
                break
            case 'bottomRight':
                bottomLeft.y(anchorY)
                topRight.x(anchorX)
                break
            case 'bottomLeft':
                bottomRight.y(anchorY)
                topLeft.x(anchorX)
                break
        }

        image.position(topLeft.position())

        var width = topRight.x() - topLeft.x()
        var height = bottomLeft.y() - topLeft.y()
        if (width && height) {
            image.width(width)
            image.height(height)
        }
    }

    addAnchor(group: any, x: number, y: number, name: string) {
        var anchor = new Konva.Circle({
            x: x,
            y: y,
            fill: '#ddd',
            radius: 10,
            name: name,
            draggable: true,
            dragOnTop: false,
            visible: ['topRight', 'bottomLeft'].includes(name) ? false : true,
        })

        anchor.on('dragmove', () => {
            this.update(anchor)
        })
        anchor.on('mousedown touchstart', function () {
            group.draggable(false)
            this.moveToTop()
        })
        anchor.on('dragend', function () {
            group.draggable(true)
        })
        anchor.on('mouseover', function () {
            document.body.style.cursor = 'pointer'
            this.strokeWidth(4)
        })
        anchor.on('mouseout', function () {
            document.body.style.cursor = 'default'
            this.strokeWidth(2)
        })

        group.add(anchor)
    }
}
